import type { RouteRecordRaw } from "vue-router";
import { PermissionAttributesEnum } from "@verbleif/lib/src/composables/Permissions/permissions.enum";
import { useAuthStore } from "@verbleif/shared/src/composables/AuthStore/useAuthStore";
import { useReportViews } from "@verbleif/shared/src/composables/ReportViews/useReportViews";
import { REPORT_PER_OBJECT_DEFAULT_VIEW } from "@verbleif/shared/src/composables/ScopedViews/reportViewSidebarLabels";
import {
  BUILTIN_REPORT_VIEW_KEYS,
  isKnownReportViewKey,
  REPORT_VIEW_FALLBACK_KEY,
  ReportListType,
  reportViewRequiresScopeParam,
} from "@web/features/Reports/ReportViewKeys";
import {
  legacyReportListRedirect,
  migrateLegacyReportViewAndIdQuery as migrateQuery,
  normalizeLegacyReportViewQuery,
} from "@web/features/Reports/Views/legacyReportListRouting";
import {
  parseReportViewScopeFromQuery,
  REPORT_PROPERTY_BROWSE_QUERY_KEY,
  REPORT_VIEW_SCOPE_QUERY_BY_TYPE,
} from "@web/features/Reports/Views/reportViewScopeQuery";
import { startViewQuery } from "@web/features/Views/defaultViewQuery";
import { isFocusedOnReportShortcut } from "./Overview/useReportList";

export {
  BUILTIN_REPORT_VIEW_KEYS,
  REPORT_VIEW_FALLBACK_KEY as FALLBACK_TYPE,
  ReportListType,
  type ReportViewKey,
} from "@web/features/Reports/ReportViewKeys";

export { isStatusScopedReportViewKey as isStatusScopedReportListType } from "@web/features/Reports/ReportViewKeys";

export { LEGACY_REPORT_LIST_TYPE_MAP, legacyReportListRedirect } from "@web/features/Reports/Views/legacyReportListRouting";

export function isReportListType(value: unknown): value is ReportListType {
  return typeof value === "string" && (Object.values(ReportListType) as string[]).includes(value);
}

export function reportListRequiresId(type: ReportListType | null): boolean {
  return type === ReportListType.TYPE_PER_PROPERTY
    || type === ReportListType.TYPE_REPORT_TOPIC
    || type === ReportListType.TYPE_REPORT_SORT;
}

export const shortcutReportListTypeMap = {
  [PermissionAttributesEnum.ContextReportshortcutAllreports]: ReportListType.TYPE_ALL,
  [PermissionAttributesEnum.ContextReportshortcutOpenreports]: ReportListType.TYPE_OPEN,
  [PermissionAttributesEnum.ContextReportshortcutClosedreports]: ReportListType.TYPE_CLOSED,
  [PermissionAttributesEnum.ContextReportshortcutMyreports]: ReportListType.TYPE_CREATED_BY_ME,
  [PermissionAttributesEnum.ContextReportshortcutGuestreports]: ReportListType.TYPE_GUEST,
  [PermissionAttributesEnum.ContextReportshortcutPerproperty]: ReportListType.TYPE_PER_PROPERTY,
  [PermissionAttributesEnum.ContextReportshortcutTransferredtotask]: ReportListType.TYPE_TRANSFERRED,
  [PermissionAttributesEnum.ContextReportshortcutTopics]: ReportListType.TYPE_REPORT_TOPIC,
  [PermissionAttributesEnum.ContextReportshortcutSorts]: ReportListType.TYPE_REPORT_SORT,
};

export const shortcutReportViewKeyMap: Record<string, string> = Object.fromEntries(
  Object.entries(shortcutReportListTypeMap).map(([perm, type]) => {
    if (type === ReportListType.TYPE_CREATED_BY_ME) {
      return [perm, BUILTIN_REPORT_VIEW_KEYS.CREATED_BY_ME];
    }
    if (type === ReportListType.TYPE_REPORT_TOPIC || type === ReportListType.TYPE_REPORT_SORT || type === ReportListType.TYPE_PER_PROPERTY) {
      return [perm, BUILTIN_REPORT_VIEW_KEYS.ALL];
    }
    return [perm, normalizeLegacyReportViewQuery(type)];
  }),
);

function firstEnabledViewWithoutScopeRequirement() {
  const { enabledViews } = useReportViews();
  return enabledViews.value.find(v => !v.definition.requiredScope)?.key ?? REPORT_VIEW_FALLBACK_KEY;
}

/** The per-object list: all reports, with the per-object section asking for an object. */
export const REPORT_PER_OBJECT_LIST_QUERY: Readonly<Record<string, string>> = {
  view: BUILTIN_REPORT_VIEW_KEYS.ALL,
  [REPORT_PROPERTY_BROWSE_QUERY_KEY]: "1",
};

/**
 * Where the list opens when the URL names no view. Without view management a user with exactly one
 * shortcut permission keeps starting on that shortcut; everyone else, and everyone with view
 * management, starts on the default list the API resolved. The per-object list is a default that is
 * not a view: no view carries `default` then, and the effective preference names it.
 */
function reportStartViewQuery(query: Record<string, unknown>): Record<string, string> {
  const { views, viewManagement } = useReportViews();
  if (!viewManagement.value) {
    const focusedShortcut = isFocusedOnReportShortcut.value;
    const viewFromShortcut = focusedShortcut ? shortcutReportViewKeyMap[focusedShortcut] : undefined;
    if (viewFromShortcut) {
      return { view: viewFromShortcut };
    }
  }
  const { userPreferences } = useAuthStore();
  if (userPreferences.value?.defaultReportView === REPORT_PER_OBJECT_DEFAULT_VIEW) {
    return { ...REPORT_PER_OBJECT_LIST_QUERY };
  }
  return startViewQuery(views.value, query, REPORT_VIEW_SCOPE_QUERY_BY_TYPE, REPORT_VIEW_FALLBACK_KEY);
}

const VIEWS_READY_TIMEOUT_MS = 5_000;

/** Waits for the effective views so deep links to custom views are not bounced to the fallback while loading. */
function awaitReportViewsReady(): Promise<void> {
  const { whenReady } = useReportViews();
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  return Promise.race([
    whenReady().finally(() => {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
    }),
    new Promise<void>((resolve) => {
      timeoutId = setTimeout(resolve, VIEWS_READY_TIMEOUT_MS);
    }),
  ]);
}

export const routes = <RouteRecordRaw[]>[
  { path: "/r/li/:type/:id?/:reportId?", redirect: legacyReportListRedirect },
  { path: "/r", redirect: { name: "report-list" } },
  {
    path: "/reports",
    name: "reports",
    component: () => import("@web/features/Reports/ReportsContainer.vue"),
    redirect: to => ({ name: "report-list", query: to.query }),
    children: [
      {
        path: ":reportId(new|\\d+)?",
        name: "report-list",
        component: () => import("./Overview/ReportsView.vue"),
        beforeEnter: async (to) => {
          await awaitReportViewsReady();
          const migrated = migrateQuery(to.query as Record<string, unknown>);
          if (migrated.view === undefined) {
            return { name: "report-list", params: to.params, query: { ...migrated, ...reportStartViewQuery(migrated) } };
          }
          const viewRaw = migrated.view;
          const view = normalizeLegacyReportViewQuery(viewRaw);
          const { byKey, enabledViews, viewManagement } = useReportViews();
          const enabledKeys = enabledViews.value.map(v => v.key);
          const known = isKnownReportViewKey(view, enabledKeys.length ? enabledKeys : undefined)
            || isKnownReportViewKey(view);

          if (!known) {
            return {
              name: "report-list",
              params: to.params,
              query: { ...migrated, view: firstEnabledViewWithoutScopeRequirement() },
            };
          }

          const effective = byKey.value.get(view);
          const scope = parseReportViewScopeFromQuery(migrated);
          if (effective?.definition.requiredScope && !reportViewRequiresScopeParam(effective.definition.requiredScope, scope)) {
            return {
              name: "report-list",
              params: to.params,
              query: { view: firstEnabledViewWithoutScopeRequirement() },
            };
          }

          const focusedShortcut = viewManagement.value ? null : isFocusedOnReportShortcut.value;
          if (focusedShortcut) {
            const viewFromShortcut = shortcutReportViewKeyMap[focusedShortcut];
            if (viewFromShortcut && view !== viewFromShortcut) {
              return { name: "report-list", params: to.params, query: { ...migrated, view: viewFromShortcut } };
            }
          }

          const needsMigrate = JSON.stringify(migrated) !== JSON.stringify({ ...to.query, view })
            || !to.query.view;
          if (needsMigrate || view !== viewRaw) {
            return { name: "report-list", params: to.params, query: { ...migrated, view } };
          }

          return true;
        },
      },
    ],
  },
];

export default routes;
